// A Mongo ObjectId as it arrives over the wire: 24 hex characters.
const OBJECT_ID = /^[0-9a-fA-F]{24}$/;

/**
 * True when `id` can identify a record that actually exists in the database.
 *
 * Training tours (sopui/src/training/*.js) push mock records through the same
 * components as real data — `room-feeding-tour-mock`, `rf-mock-sop-feedingproto`,
 * `rf-mock-doc` and ~600 siblings. None of them are ObjectIds, so any request
 * made with one is guaranteed to come back 500 with a Mongoose CastError and to
 * fill the production logs with noise. Shared components that fetch by id gate
 * their loads on this so mock ids never reach the API. Real ids re-enable the
 * fetch on the next render once the tour restores the live record.
 *
 * Accepts a populated object too (`{ _id, name, ... }`), which is how the API
 * returns references such as a room feeding's finalDocument.
 */
export function isPersistedId(id) {
    const value = (id != null && typeof id === 'object') ? id._id : id;
    return typeof value === 'string' && OBJECT_ID.test(value);
}

// ---------------------------------------------------------------------------
// Mock-id registry
// ---------------------------------------------------------------------------
// isPersistedId() above is the per-call-site guard: a component that knows it
// is about to build a record-scoped URL checks the id first. That only covers
// the call sites we have found. This registry is the net under the rest.
//
// Every id a training module hands the app is remembered here, and
// tourInterceptor.js drops any request whose URL carries one — so a screen we
// have not audited, or a training file written next month, cannot put a mock
// id on the wire. Entries are never removed: a mock id is poison before,
// during AND after the tour that introduced it. That matters, because the
// failing fetch is often the one racing tour teardown — clicking Attachments
// or Comments lands outside Joyride's spotlight, which ENDS the tour, so the
// request goes out in the same tick that the real record id is being restored.
// Anything gated on "is a tour running" would lose that race.
const mockIds = new Set();

// Below this length an id isn't distinctive enough to match URL tokens against
// without risking a collision with a real path segment.
const MIN_MOCK_ID_LENGTH = 4;

function remember(value) {
    if (typeof value !== 'string') return;
    const id = value.trim();
    if (id.length < MIN_MOCK_ID_LENGTH) return;
    if (isPersistedId(id)) return;      // a real id inside the mock — leave it alone
    if (/[\s/?&#]/.test(id)) return;    // not a single URL token; can't match safely
    mockIds.add(id);
}

/**
 * Walk a training payload and remember its fake ids.
 *
 * Two collection rules, kept deliberately narrow so an ordinary string (a room
 * name, a status label) is never poisoned and never starts blocking real
 * traffic:
 *   - the value of an `_id` / `id` key, at any depth; and
 *   - any string containing 'tour-mock', wherever it sits — that picks up the
 *     reference-only ids (training/bin.js's `location: 'tour-mock-loc'`) which
 *     never appear as an `_id` of their own.
 *
 * Reference fields pointing at a mocked child (`document: 'rf-mock-doc'`) need
 * no rule of their own: the child's `_id` is collected by the first rule and
 * matching is by value.
 */
export function harvestMockIds(payload) {
    const seen = new Set();
    const walk = (node) => {
        if (node == null) return;
        if (typeof node === 'string') {
            if (node.includes('tour-mock')) remember(node);
            return;
        }
        if (typeof node !== 'object' || seen.has(node)) return;
        seen.add(node);                 // training mocks reuse row objects across steps
        if (Array.isArray(node)) {
            node.forEach(walk);
            return;
        }
        for (const [key, value] of Object.entries(node)) {
            if ((key === '_id' || key === 'id') && typeof value === 'string') remember(value);
            walk(value);
        }
    };
    try {
        walk(payload);
    } catch {
        // Best-effort — a pathological payload must not take the tour down.
    }
}

/** True if this exact string has been seen as an id in training data. */
export function isMockId(value) {
    return typeof value === 'string' && mockIds.has(value);
}

/**
 * Does this URL carry a mock id where the server would try to cast one?
 * Whole-token matching (split on URL punctuation), so a registered id can
 * never match a fragment of an unrelated path.
 */
export function urlCarriesMockId(url) {
    if (typeof url !== 'string' || url === '' || mockIds.size === 0) return false;
    for (const token of url.split(/[/?&=#]+/)) {
        if (token === '') continue;
        if (mockIds.has(token)) return true;
        let decoded = token;            // ids also travel URL-encoded in query strings
        try {
            decoded = decodeURIComponent(token);
        } catch {
            continue;
        }
        if (decoded !== token && mockIds.has(decoded)) return true;
    }
    return false;
}

/** Escape hatch for an id the harvester can't reach (and for tests). */
export function registerMockId(value) {
    remember(value);
}

const idHelper = { isPersistedId, harvestMockIds, isMockId, urlCarriesMockId, registerMockId };

export default idHelper;
